<template>
    <div>
        <rkm-form ref="Form">
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Data de entrada"
                        end-label="Data de saída"
                        :start-validations="validations.inicio"
                        :end-validations="validations.fim"
                        v-model="form.periodo"
                    />
                </rkm-col>

                <rkm-col class="flex-grow-1">
                    <rkm-form-select
                        label="Unidade"
                        :options="form.unidades"
                        text-field="nomeFantasia"
                        value-field="id"
                        v-model="form.unidadeId"
                        :validations="validations.unidade"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col class="flex-grow-0">
                    <rkm-button
                        :label="false"
                        :color="advancedSearch ? 'primary' : 'secondary'"
                        title="Busca avançada"
                        @click.stop="advancedSearch = !advancedSearch"
                    >
                        <rkm-icon icon="filter" />
                    </rkm-button>
                </rkm-col>
            </rkm-row>

            <template v-if="advancedSearch">
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Local de atendimento"
                            :options="infraestruturas"
                            text-field="nome"
                            value-field="id"
                            v-model="form.infraestruturaId"
                            show-default-option
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Profissional"
                            :options="usuarios"
                            v-model="form.usuarioId"
                            text-field="nome"
                            value-field="id"
                            show-default-option
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            label="CBO"
                            :options="ocupacoes"
                            text-field="codigoNome"
                            value-field="id"
                            v-model="form.ocupacaoId"
                            show-default-option
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Serviço"
                            :options="form.servicos"
                            text-field="nome"
                            value-field="id"
                            v-model="form.servicoId"
                            default-option-text="Selecione"
                            show-default-option
                        />
                    </rkm-col>
                </rkm-row>
            </template>

            <rkm-row>
                <rkm-col
                    v-if="advancedSearch"
                    cols="6"
                >
                    <rkm-form-select
                        label="Status"
                        :options="form.status"
                        text-field="nome"
                        value-field="id"
                        v-model="form.statusId"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col
                    :cols="advancedSearch ? 'auto' : 6"
                    :class="{ 'flex-grow-1': advancedSearch }"
                >
                    <rkm-form-select
                        label="Ordenar por"
                        v-model="form.ordenacaoValue"
                        :options="form.ordenacao"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col class="flex-grow-0 text-nowrap">
                    <rkm-button
                        :label="false"
                        color="primary"
                        @click.stop="search"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click.stop="resetForm"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template v-if="hasPlantoes">
            <div class="d-flex justify-content-between mb-2">
                <div>
                    <rkm-button
                        v-if="showSelectAllButton"
                        color="primary"
                        @click="selectAll"
                    >
                        {{ selectAllTextButton }}
                    </rkm-button>

                    <template v-if="showActionButtons">
                        <rkm-button
                            color="outline-success"
                            @click.stop="confirmSelected"
                            authorize="update-pronto-atendimento-plantao"
                        >
                            Confirmar
                        </rkm-button>

                        <rkm-button
                            color="danger"
                            @click.stop="deleteSelected"
                            authorize="destroy-pronto-atendimento-plantao"
                        >
                            Excluir
                        </rkm-button>
                    </template>
                </div>

                <div>
                    <rkm-button :id="uniqueId('btn-color-subtitle')">
                        Legenda das cores
                    </rkm-button>

                    <rkm-popover
                        :target="uniqueId('btn-color-subtitle')"
                        triggers="hover"
                        placement="leftbottom"
                    >
                        <rkm-row
                            v-for="status in form.status"
                            :key="status.id"
                            class="flex-nowrap"
                        >
                            <rkm-col class="flex-grow-0 pr-0">
                                <div v-bind="getSubtitleProps(status)"></div>
                            </rkm-col>

                            <rkm-col class="flex-grow-1 text-nowrap">
                                <span>{{ status.nome }}</span>
                            </rkm-col>
                        </rkm-row>
                    </rkm-popover>
                </div>
            </div>

            <rkm-list
                :items="plantoes"
                item-cursor="default"
                hide-filters
                hide-pagination
            >
                <template v-slot="{ item }">
                    <rkm-row class="flex-nowrap">
                        <rkm-col class="flex-grow-0 my-auto text-center">
                            <rkm-form-checkbox
                                :style="{ 'visibility': isPlantaoCadastrado(item) ? 'visible' : 'hidden' }"
                                v-model="item.checked"
                            />
                        </rkm-col>

                        <rkm-col class="flex-grow-1">
                            <rkm-row>
                                <rkm-col cols="2">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Data/hora entrada</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.inicio }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="3">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Data/hora saída</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.fim }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="4">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Unidade</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.unidadeNomeFantasia }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="3">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Local de atendimento</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.infraestruturaNome }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>
                            </rkm-row>

                            <rkm-row>
                                <rkm-col cols="5">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Serviço</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.nomeServico }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="4">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Profissional</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.cidadaoNome }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="3">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Perfil de atendimento</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.faixaEtariaNome }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>
                            </rkm-row>

                            <rkm-row>
                                <rkm-col cols="5">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>CBO</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.ocupacaoCodigoNome }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col cols="4">
                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                <strong>Número de atendimentos</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ item.quantidadeAtendimentos }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-col>

                                <rkm-col
                                    cols="3"
                                    class="d-flex justify-content-end"
                                >
                                    <rkm-dropdown
                                        v-if="isPlantaoCadastrado(item)"
                                        color="primary"
                                        position="left"
                                        class="align-self-end"
                                        no-caret
                                        :disabled="item.checked"
                                    >
                                        <template #button-content>
                                            <rkm-icon icon="wrench" />
                                        </template>

                                        <rkm-dropdown-item
                                            @click.stop="$emit('click-to-update', item.id)"
                                            authorize="update-pronto-atendimento-plantao"
                                        >
                                            Alterar
                                        </rkm-dropdown-item>

                                        <rkm-dropdown-item
                                            @click.stop="handleConfirmacao(item)"
                                            authorize="update-pronto-atendimento-plantao"
                                        >
                                            Confirmar
                                        </rkm-dropdown-item>

                                        <rkm-dropdown-item
                                            @click.stop="handlePlantonistaAusente(item)"
                                            authorize="update-pronto-atendimento-plantao"
                                        >
                                            Plantonista ausente
                                        </rkm-dropdown-item>

                                        <rkm-dropdown-item
                                            @click.stop="remove(item)"
                                            authorize="destroy-pronto-atendimento-plantao"
                                        >
                                            Excluir
                                        </rkm-dropdown-item>
                                    </rkm-dropdown>

                                    <rkm-button
                                        v-else-if="isPlantaoComAtendimentoAgendado(item)"
                                        color="primary"
                                        class="align-self-end"
                                        block
                                        @click="$emit('click-to-update', item.id)"
                                        authorize="update-pronto-atendimento-plantao"
                                    >
                                        Alterar
                                    </rkm-button>

                                    <rkm-button
                                        v-else-if="isPlantaoConfirmado(item)"
                                        color="primary"
                                        class="align-self-end"
                                        block
                                        @click="handleDesfazerConfirmacao(item)"
                                        authorize="update-pronto-atendimento-plantao"
                                    >
                                        Desfazer confirmação
                                    </rkm-button>

                                    <rkm-button
                                        v-else-if="isPlantaoPlantonistaAusente(item)"
                                        color="primary"
                                        class="align-self-end"
                                        block
                                        @click="handleDesfazerAusencia(item)"
                                        authorize="update-pronto-atendimento-plantao"
                                    >
                                        Desfazer ausência
                                    </rkm-button>
                                </rkm-col>
                            </rkm-row>
                        </rkm-col>
                    </rkm-row>
                </template>
            </rkm-list>
        </template>
    </div>
</template>

<script>
// Mixins.
import { repository, error, data } from '@/mixins'

// Enums.
import status from '@/enums/pronto-atendimento/plantao/status'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Moment.
import moment from 'moment'

export default {
    mixins: [ repository, error, data ],
    data: () => ({
        advancedSearch: false,
        dataAtual: '',
        repositories: {
            plantao: null,
        },
        form: {
            periodo: {
                start: null,
                end: null,
            },
            unidades: [],
            unidadeId: null,
            infraestruturaId: null,
            servicos: [],
            servicoId: null,
            usuarioId: null,
            ocupacaoId: null,
            status: [],
            statusId: null,
            ordenacao: [],
            ordenacaoValue: 'inicio',
        },
        plantoes: [],
        enums: { status },
    }),
    computed: {
        validations() {

            return {
                inicio: { required: true },
                fim: { required: true },
                unidade: { required: true },
            }
        },
        infraestruturas() {

            if (this.form.unidadeId) {

                return this.form.unidades.find(u => u.id === this.form.unidadeId).infraestruturas
            }

            return []
        },
        usuarios() {

            if (this.form.unidadeId && this.form.infraestruturaId) {

                return this.infraestruturas.find(i => i.id === this.form.infraestruturaId).usuarios
            }

            return []
        },
        ocupacoes() {

            if (this.form.unidadeId && this.form.infraestruturaId && this.form.usuarioId) {

                return this.usuarios.find(u => u.id === this.form.usuarioId).ocupacoes
            }

            return []
        },
        hasPlantoes() {

            return this.plantoes.length > 0
        },
        allSelected() {

            return !this.plantoes.find(p => p.statusId === this.enums.status.CADASTRADO && !p.checked)
        },
        showSelectAllButton() {

            return this.plantoes.find(p => p.statusId === this.enums.status.CADASTRADO)
        },
        selectAllTextButton() {

            return this.allSelected ? 'Desmarcar todos' : 'Selecionar todos'
        },
        showActionButtons() {

            return this.plantoes.find(p => p.checked)
        },
    },
    watch: {
        'infraestruturas': function (infraestruturas) {

            if (this.advancedSearch && infraestruturas.length === 1) {

                this.$nextTick(() => this.form.infraestruturaId = infraestruturas[0].id)
            }
        },
        'servicos': function (servicos) {

            if (this.advancedSearch && servicos.length === 1) {

                this.$nextTick(() => this.form.servicoId = servicos[0].id)
            }
        },
        'usuarios': function (usuarios) {

            if (this.advancedSearch && usuarios.length === 1) {

                this.$nextTick(() => this.form.usuarioId = usuarios[0].id)
            }
        },
        'ocupacoes': function (ocupacoes) {

            if (this.advancedSearch && ocupacoes.length === 1) {

                this.$nextTick(() => this.form.ocupacaoId = ocupacoes[0].id)
            }
        },
        advancedSearch(advanced) {

            if (advanced) {

                if (this.infraestruturas.length === 1) {

                    this.$nextTick(() => this.form.infraestruturaId = this.infraestruturas[0].id)
                }
            }
            else {

                this.form.infraestruturaId = null
                this.form.statusId = null
            }
        },
        'form.unidadeId': function () {

            this.form.infraestruturaId = null
        },
        'form.infraestruturaId': function () {

            this.form.setorId = null
            this.form.usuarioId = null
        },
        'form.usuarioId': function () {

            this.form.ocupacaoId = null
        },
    },
    created() {

        this.repositories.plantao = this.getConectaSusRepository('prontoAtendimento.plantao')

        this.load()
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        ...mapGetters(['getLotacao']),
        load() {

            this.setLoading()

            this.repositories.plantao.list()
                .then(({ data }) => {

                    Object.assign(this.form, data.form)

                    this.dataAtual = data.dataAtual

                    this.setDefaultValues()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    const filters = {
                        inicio: `${this.getFormatedDate(this.form.periodo.start)} 00:00`,
                        fim: `${this.getFormatedDate(this.form.periodo.end)} 23:59`,
                        unidade_id: this.form.unidadeId,
                        infraestrutura_id: this.form.infraestruturaId,
                        servico_id: this.form.servicoId,
                        usuario_id: this.form.usuarioId,
                        ocupacao_id: this.form.ocupacaoId,
                        status_id: this.form.statusId,
                        order_by: this.form.ordenacaoValue,
                    }

                    this.setLoading()

                    this.repositories.plantao.datatable(filters)
                        .then(({ data }) => this.plantoes = data.map(plantao => {

                            this.setPlantaoStyle(plantao)

                            plantao.checked = false

                            return plantao
                        }))
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        setPlantaoStyle(plantao) {

            const color = this.form.status.find(s => s.id === plantao.statusId).cor

            plantao._listItemColor = color
        },
        resetForm() {

            this.form.unidadeId = null
            this.form.servicoId = null
            this.form.statusId = null
            this.form.ordenacaoValue = null

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()

                    return this.$nextTick()
                })
                .then(() => this.$refs.Form.resetValidator())
        },
        setDefaultValues() {

            this.form.periodo.start = moment(this.dataAtual, 'DD/MM/YYYY').toDate()
            this.form.periodo.end = moment(this.dataAtual, 'DD/MM/YYYY').toDate()

            this.form.unidadeId = this.getLotacao().unidadeId
            this.form.ordenacaoValue = 'inicio'
        },
        selectAll() {

            const check = !this.allSelected

            this.plantoes.map(p => p.statusId === this.enums.status.CADASTRADO && (p.checked = check))
        },
        getSubtitleProps(status) {

            const classes = ['content-subtitle-item-color']
            const style = {}

            if (status === this.enums.status.CADASTRADO) {

                style.border = '1px solid rgba(0, 0, 0, 0.50) !important'
            }
            else {

                classes.push(`list-group-item-${status.cor}`)
            }

            return { class: classes, style }
        },
        isPlantaoCadastrado(plantao) {

            return plantao.statusId === this.enums.status.CADASTRADO
        },
        isPlantaoConfirmado(plantao) {

            return plantao.statusId === this.enums.status.CONFIRMADO
        },
        isPlantaoComAtendimentoAgendado(plantao) {

            return plantao.statusId === this.enums.status.COM_ATENDIMENTO_AGENDADO
        },
        isPlantaoPlantonistaAusente(plantao) {

            return plantao.statusId === this.enums.status.PLANTONISTA_AUSENTE
        },
        confirmSelected() {

            const plantoes = this.plantoes.filter(p => p.checked)

            const data = plantoes.map(p => ({
                id: p.id,
                status_id: this.enums.status.CONFIRMADO,
            }))

            const confirm = () => this.repositories.plantao.updateAllStatus(data)
                .then(({ data }) => data.map(plantaoUpdated => {

                    const plantao = this.plantoes.find(p => p.id === plantaoUpdated.id)

                    plantao.statusId = plantaoUpdated.statusId

                    this.setPlantaoStyle(plantao)

                    plantao.checked = false
                }))

            this.showConfirmation(
                'Tem certeza que deseja confirmar todos os plantões selecionados?',
                confirm,
                null,
                this.handleError
            )
        },
        deleteSelected() {

            const plantoes = this.plantoes.filter(p => p.checked)

            const destroy = () => this.repositories.plantao.destroyAll(plantoes.map(p => p.id))
                .then(() => plantoes.map(plantao => this.plantoes.splice(this.plantoes.indexOf(plantao), 1)))

            this.showDeleteConfirmation(
                'Tem certeza que deseja excluir todos os plantões selecionados?',
                destroy,
                this.handleError
            )
        },
        handleConfirmacao(plantao) {

            this.showConfirmation(
                'Tem certeza que deseja confirmar o plantão?',
                () => this.repositories.plantao.patch(plantao.id, { status_id: this.enums.status.CONFIRMADO })
                    .then(({ data }) => {

                        plantao.statusId = data.statusId

                        this.setPlantaoStyle(plantao)
                    }),
                null,
                this.handleError
            )
        },
        handleDesfazerConfirmacao(plantao) {

            this.showConfirmation(
                'Tem certeza que deseja desfazer confirmação do plantão?',
                () => this.repositories.plantao.patch(plantao.id, { status_id: this.enums.status.CADASTRADO })
                    .then(({ data }) => {

                        plantao.statusId = data.statusId

                        this.setPlantaoStyle(plantao)
                    }),
                null,
                this.handleError
            )
        },
        handlePlantonistaAusente(plantao) {

            this.showConfirmation(
                'Tem certeza que deseja alterar para plantonista ausente?',
                () => this.repositories.plantao.patch(plantao.id, { status_id: this.enums.status.PLANTONISTA_AUSENTE })
                    .then(({ data }) => {

                        plantao.statusId = data.statusId

                        this.setPlantaoStyle(plantao)
                    }),
                null,
                this.handleError
            )
        },
        handleDesfazerAusencia(plantao) {

            this.showConfirmation(
                'Tem certeza que deseja desfazer ausência do plantonista',
                () => this.repositories.plantao.patch(plantao.id, { status_id: this.enums.status.CADASTRADO })
                    .then(({ data }) => {

                        plantao.statusId = data.statusId

                        this.setPlantaoStyle(plantao)
                    }),
                null,
                this.handleError
            )
        },
        remove(plantao) {

            this.showDeleteConfirmation(
                'Tem certeza que deseja excluir o plantão?',
                () => this.repositories.plantao.destroy(plantao.id)
                    .then(() => this.plantoes.splice(this.plantoes.indexOf(plantao), 1)),
                this.handleError
            )
        },
    },
}
</script>

<style scoped>
.content-subtitle-item-color {
    width: 30px;
    height: 20px;
    border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>